import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  Shield, ShieldAlert, AlertTriangle, Scale,
  CheckCircle2, AlertCircle, XCircle,
  FileText, Users, ClipboardCheck, Wrench,
  ChevronRight, RefreshCw,
} from "lucide-react";
import moment from "moment";

// ── Helpers ────────────────────────────────────────────────────────────────

function statusLevel(value, thresholds = { green: 0, yellow: 1 }) {
  if (value <= thresholds.green) return "green";
  if (value <= thresholds.yellow) return "yellow";
  return "red";
}

const LEVEL_STYLES = {
  green:  { dot: "bg-emerald-500", bar: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "Correcte" },
  yellow: { dot: "bg-amber-400",   bar: "bg-amber-400",   text: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-200",   label: "Revisar"  },
  red:    { dot: "bg-red-500",     bar: "bg-red-500",     text: "text-red-700",     bg: "bg-red-50",     border: "border-red-200",     label: "Pendent"  },
};

function StatusDot({ level, size = "md" }) {
  const s = LEVEL_STYLES[level] || LEVEL_STYLES.green;
  const sizeClass = size === "sm" ? "w-2 h-2" : "w-3 h-3";
  return <span className={`inline-block rounded-full flex-shrink-0 ${sizeClass} ${s.dot}`} />;
}

function AreaCard({ icon: Icon, title, page, level, indicators }) {
  const s = LEVEL_STYLES[level] || LEVEL_STYLES.green;
  return (
    <Link to={createPageUrl(page)}>
      <Card className={`p-5 border shadow-sm hover:shadow-md transition-all cursor-pointer ${s.bg} ${s.border}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.bg} border ${s.border}`}>
              <Icon className={`w-5 h-5 ${s.text}`} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">{title}</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <StatusDot level={level} />
                <span className={`text-xs font-semibold ${s.text}`}>{s.label}</span>
              </div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
        </div>
        <div className="space-y-2">
          {indicators.map((ind, i) => (
            <IndicatorRow key={`ind-${i}`} {...ind} />
          ))}
        </div>
      </Card>
    </Link>
  );
}

function IndicatorRow({ label, value, level }) {
  const s = LEVEL_STYLES[level] || LEVEL_STYLES.green;
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-600">{label}</span>
      <div className="flex items-center gap-1.5">
        <StatusDot level={level} size="sm" />
        <span className={`text-xs font-semibold ${s.text}`}>{value}</span>
      </div>
    </div>
  );
}

function SummaryBar({ total, green, yellow, red }) {
  const gPct = total > 0 ? (green / total) * 100 : 0;
  const yPct = total > 0 ? (yellow / total) * 100 : 0;
  const rPct = total > 0 ? (red / total) * 100 : 0;
  return (
    <div className="flex w-full h-3 rounded-full overflow-hidden bg-gray-100">
      {gPct > 0 && <div className="bg-emerald-500 h-full transition-all" style={{ width: `${gPct}%` }} />}
      {yPct > 0 && <div className="bg-amber-400 h-full transition-all" style={{ width: `${yPct}%` }} />}
      {rPct > 0 && <div className="bg-red-500 h-full transition-all" style={{ width: `${rPct}%` }} />}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function ComplianceDashboard() {
  const { currentVenue, userRole, loading: venueLoading } = useVenue();
  const [areas, setAreas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    if (currentVenue) loadData();
  }, [currentVenue]);

  const loadData = async () => {
    setLoading(true);
    const vid = currentVenue.id;
    const today = moment().format("YYYY-MM-DD");

    const [
      // RGPD
      processingActivities,
      dataConsents,
      dataIncidents,
      // APPCC
      appccPlans,
      hazardAnalyses,
      ccps,
      correctionActions,
      appccVerifications,
      // PRL
      safetyDocs,
      safetyTrainings,
      workIncidents,
      equipmentMaint,
      // Assetjament
      harassmentCases,
      harassmentTrainings,
    ] = await Promise.all([
      hosys.entities.DataProcessingActivity.filter({ venue_id: vid }),
      hosys.entities.DataConsent.filter({ venue_id: vid }),
      hosys.entities.DataIncident.filter({ venue_id: vid }),
      hosys.entities.APPCCPlan.filter({ venue_id: vid }),
      hosys.entities.HazardAnalysis.filter({ venue_id: vid }),
      hosys.entities.CriticalControlPoint.filter({ venue_id: vid }),
      hosys.entities.CorrectionAction.filter({ venue_id: vid }),
      hosys.entities.APPCCVerification.filter({ venue_id: vid }),
      hosys.entities.SafetyDocument.filter({ venue_id: vid }),
      hosys.entities.SafetyTraining.filter({ venue_id: vid }),
      hosys.entities.WorkIncident.filter({ venue_id: vid }),
      hosys.entities.EquipmentMaintenance.filter({ venue_id: vid }),
      hosys.entities.HarassmentCase.filter({ venue_id: vid }),
      hosys.entities.HarassmentTraining.filter({ venue_id: vid }),
    ]);

    // ── RGPD ──────────────────────────────────────────────────────
    const rgpdPendingDocs = processingActivities.filter(a => a.status !== "active").length;
    const rgpdOpenIncidents = dataIncidents.filter(d => d.status !== "resolved").length;
    const rgpdWithdrawnConsents = dataConsents.filter(c => c.withdrawn).length;
    const rgpdLevel = statusLevel(rgpdOpenIncidents + rgpdPendingDocs, { green: 0, yellow: 2 });

    // ── APPCC ─────────────────────────────────────────────────────
    const activePlan = appccPlans.find(p => p.status === "active");
    const openCorrections = correctionActions.filter(a => a.status === "pending" || a.status === "completed").length;
    const pendingVerifications = appccVerifications.filter(v => v.status === "pending_actions").length;
    const activeCCPs = ccps.filter(c => c.status === "active").length;
    const appccLevel = !activePlan ? "red" : statusLevel(openCorrections + pendingVerifications, { green: 0, yellow: 2 });

    // ── PRL ───────────────────────────────────────────────────────
    const openWorkIncidents = workIncidents.filter(i => i.status !== "closed").length;
    const criticalWorkIncidents = workIncidents.filter(i => i.severity === "critical" && i.status !== "closed").length;
    const expiredTrainings = safetyTrainings.filter(t => t.status === "expired").length;
    const pendingMaint = equipmentMaint.filter(m => m.status === "pending" || m.status === "needs_repair").length;
    const expiredDocs = safetyDocs.filter(d => d.expiry_date && moment(d.expiry_date).isBefore(today)).length;
    const prlLevel = criticalWorkIncidents > 0 ? "red" : statusLevel(openWorkIncidents + expiredTrainings + pendingMaint, { green: 0, yellow: 2 });

    // ── Assetjament ───────────────────────────────────────────────
    const openCases = harassmentCases.filter(c => c.status === "received" || c.status === "investigating").length;
    const expiredHarTrainings = harassmentTrainings.filter(t =>
      t.expiry_date && moment(t.expiry_date).isBefore(today)
    ).length;
    const harassLevel = statusLevel(openCases, { green: 0, yellow: 1 });

    setAreas({
      rgpd: {
        level: rgpdLevel,
        indicators: [
          { label: "Activitats de tractament pendents", value: rgpdPendingDocs, level: statusLevel(rgpdPendingDocs, { green: 0, yellow: 1 }) },
          { label: "Incidents de dades oberts", value: rgpdOpenIncidents, level: statusLevel(rgpdOpenIncidents, { green: 0, yellow: 0 }) },
          { label: "Consentiments retirats", value: rgpdWithdrawnConsents, level: statusLevel(rgpdWithdrawnConsents, { green: 0, yellow: 2 }) },
        ],
      },
      appcc: {
        level: appccLevel,
        hasPlan: !!activePlan,
        indicators: [
          { label: "Pla APPCC actiu", value: activePlan ? "Sí" : "No", level: activePlan ? "green" : "red" },
          { label: "PCCs actius", value: activeCCPs, level: activeCCPs > 0 ? "green" : "yellow" },
          { label: "Accions correctives pendents", value: openCorrections, level: statusLevel(openCorrections, { green: 0, yellow: 2 }) },
          { label: "Verificacions amb accions pendents", value: pendingVerifications, level: statusLevel(pendingVerifications, { green: 0, yellow: 1 }) },
        ],
      },
      prl: {
        level: prlLevel,
        indicators: [
          { label: "Incidents laborals oberts", value: openWorkIncidents, level: statusLevel(openWorkIncidents, { green: 0, yellow: 1 }) },
          { label: "Incidents crítics", value: criticalWorkIncidents, level: statusLevel(criticalWorkIncidents, { green: 0, yellow: 0 }) },
          { label: "Formacions caducades", value: expiredTrainings, level: statusLevel(expiredTrainings, { green: 0, yellow: 1 }) },
          { label: "Manteniments pendents", value: pendingMaint, level: statusLevel(pendingMaint, { green: 0, yellow: 2 }) },
          { label: "Documents caducats", value: expiredDocs, level: statusLevel(expiredDocs, { green: 0, yellow: 1 }) },
        ],
      },
      harassment: {
        level: harassLevel,
        indicators: [
          { label: "Casos oberts", value: openCases, level: statusLevel(openCases, { green: 0, yellow: 0 }) },
          { label: "Total casos registrats", value: harassmentCases.length, level: "green" },
          { label: "Formacions caducades", value: expiredHarTrainings, level: statusLevel(expiredHarTrainings, { green: 0, yellow: 1 }) },
        ],
      },
    });

    setLastRefresh(new Date());
    setLoading(false);
  };

  if (venueLoading || loading) return <QuickLoader />;

  const allLevels = areas ? [areas.rgpd.level, areas.appcc.level, areas.prl.level, areas.harassment.level] : [];
  const greenCount = allLevels.filter(l => l === "green").length;
  const yellowCount = allLevels.filter(l => l === "yellow").length;
  const redCount = allLevels.filter(l => l === "red").length;

  const globalLevel = redCount > 0 ? "red" : yellowCount > 0 ? "yellow" : "green";
  const globalStyle = LEVEL_STYLES[globalLevel];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Panell de Compliment Normatiu"
        subtitle={`${currentVenue?.name} · Visió global de l'estat regulatori`}
        actions={
          <Button size="sm" variant="outline" onClick={loadData} className="gap-2">
            <RefreshCw className="w-3.5 h-3.5" /> Actualitzar
          </Button>
        }
      />

      {/* Global summary */}
      <Card className={`p-5 border ${globalStyle.border} ${globalStyle.bg} shadow-sm`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${globalStyle.bg} border-2 ${globalStyle.border}`}>
              <Shield className={`w-7 h-7 ${globalStyle.text}`} />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-widest">Estat global de compliment</p>
              <h2 className={`text-2xl font-bold ${globalStyle.text}`}>{globalStyle.label}</h2>
              {lastRefresh && <p className="text-xs text-gray-400 mt-0.5">Actualitzat: {moment(lastRefresh).format("HH:mm")}</p>}
            </div>
          </div>
          <div className="flex flex-col gap-2 min-w-[160px]">
            <SummaryBar total={4} green={greenCount} yellow={yellowCount} red={redCount} />
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />{greenCount} Correcte</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />{yellowCount} Revisar</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />{redCount} Pendent</span>
            </div>
          </div>
        </div>
      </Card>

      {/* 4 areas grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <AreaCard
          icon={Shield}
          title="Protecció de Dades (RGPD)"
          page="GDPRManagement"
          level={areas.rgpd.level}
          indicators={areas.rgpd.indicators}
        />
        <AreaCard
          icon={ClipboardCheck}
          title="Seguretat Alimentària (APPCC)"
          page="APPCCManagement"
          level={areas.appcc.level}
          indicators={areas.appcc.indicators}
        />
        <AreaCard
          icon={AlertTriangle}
          title="Riscos Laborals (PRL)"
          page="WorkerSafetyManagement"
          level={areas.prl.level}
          indicators={areas.prl.indicators}
        />
        <AreaCard
          icon={ShieldAlert}
          title="Protocol d'Assetjament"
          page="HarassmentProtocol"
          level={areas.harassment.level}
          indicators={areas.harassment.indicators}
        />
      </div>

      {/* Legend */}
      <Card className="p-4 border-0 shadow-sm bg-gray-50">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Llegenda d'indicadors</p>
        <div className="flex flex-wrap gap-4">
          {Object.entries(LEVEL_STYLES).map(([key, s]) => (
            <div key={key} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${s.dot}`} />
              <span className="text-xs text-gray-600 font-medium">{s.label}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}